import { useState } from "react";
import { Link } from "react-router-dom";
import { api, LOGO_URL, ZEFFY } from "../../lib/api";
import { toast } from "sonner";
import { Mail, Phone, Globe, Instagram, Linkedin, Facebook, Youtube, ArrowRight } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await api.post("/newsletter", { email });
      if (res.data.status === "already_subscribed") toast("Already on the list.");
      else toast.success("You're in. Watch your inbox.");
      setEmail("");
    } catch {
      toast.error("Subscription failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-black border-t border-white/10">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-20">
        <div className="grid md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="AAWTA" className="h-14 w-14 object-contain bg-white/95 rounded-full p-1.5" />
              <div>
                <p className="text-overline text-aawta-pink">AAWTA</p>
                <p className="font-display text-lg text-white leading-tight">African American Women<br/> Trucking Association</p>
              </div>
            </div>
            <p className="font-display text-2xl md:text-3xl text-white/90 leading-tight mt-8">
              The 3rd Annual<br /> <em className="italic text-aawta-pink">Power to Drive Change</em><br /> Fundraising Gala
            </p>
            <p className="text-sm text-white/50 mt-4">Workforce, Leadership &amp; Industry Impact.</p>
            <p className="text-overline text-white/40 mt-6">DEC 10, 2026 · ATLANTA</p>
          </div>

          {/* Newsletter */}
          <div className="md:col-span-4">
            <p className="text-overline text-white/60 mb-4">NEWSLETTER</p>
            <p className="text-white/70 text-sm mb-5 leading-relaxed">
              Quarterly updates on AAWTA scholarships, leadership programs, and the gala.
            </p>
            <form onSubmit={subscribe} data-testid="newsletter-form" className="flex border border-white/15 focus-within:border-aawta-pink transition-all">
              <input
                data-testid="newsletter-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                type="email"
                required
                className="flex-1 bg-transparent text-white placeholder:text-white/40 px-4 py-3 outline-none text-sm"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                disabled={submitting}
                className="bg-aawta-pink text-white px-5 disabled:opacity-50"
              >
                <ArrowRight size={16} />
              </button>
            </form>
          </div>

          {/* Contact + social */}
          <div className="md:col-span-3">
            <p className="text-overline text-white/60 mb-4">CONTACT</p>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-center gap-2"><Mail size={14} className="text-aawta-pink" /> contact@aawta.org</li>
              <li className="flex items-center gap-2"><Phone size={14} className="text-aawta-pink" /> 770-756-6768</li>
              <li className="flex items-center gap-2"><Globe size={14} className="text-aawta-pink" /> www.aawta.org</li>
            </ul>
            <div className="flex items-center gap-4 mt-6 text-white/60">
              <a aria-label="Instagram" href="https://www.instagram.com/aawta_org" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="hover:text-aawta-pink transition-colors"><Instagram size={18} /></a>
              <a aria-label="Facebook" href="https://www.facebook.com/AfricanAmericanWomenInTrucking" target="_blank" rel="noopener noreferrer" data-testid="social-facebook" className="hover:text-aawta-pink transition-colors"><Facebook size={18} /></a>
              <a aria-label="LinkedIn" href="https://www.linkedin.com/company/african-american-women-trucking-association/?viewAsMember=true" target="_blank" rel="noopener noreferrer" data-testid="social-linkedin" className="hover:text-aawta-pink transition-colors"><Linkedin size={18} /></a>
              <a aria-label="YouTube" href="https://www.youtube.com/channel/UCMjGaJ07C5qsujyvncJwL-w" target="_blank" rel="noopener noreferrer" data-testid="social-youtube" className="hover:text-aawta-pink transition-colors"><Youtube size={18} /></a>
            </div>
            <a
              href={ZEFFY.galaTier}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-sponsor"
              className="btn-blue mt-8 w-full"
            >
              BECOME A SPONSOR
            </a>
            <a
              href={ZEFFY.donate}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-donate"
              className="btn-pink mt-3 w-full"
            >
              DONATE
            </a>
          </div>
        </div>

        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/40">
          <p>© {new Date().getFullYear()} African American Women Trucking Association. All rights reserved.</p>
          <div className="flex items-center gap-5 flex-wrap">
            <a href="https://www.aawta.org/privacy" target="_blank" rel="noopener noreferrer" data-testid="footer-privacy" className="hover:text-aawta-pink transition-colors">Privacy Policy</a>
            <a href="https://www.aawta.org/terms" target="_blank" rel="noopener noreferrer" data-testid="footer-terms" className="hover:text-aawta-pink transition-colors">Terms &amp; Conditions</a>
            <a href="mailto:contact@aawta.org" data-testid="footer-contact" className="hover:text-aawta-pink transition-colors">Contact Us</a>
            <Link to="/admin/login" data-testid="footer-admin-login" className="hover:text-aawta-pink transition-colors">Admin Login</Link>
          </div>
          <p className="tracking-[0.2em] uppercase">Together, We Have The Power To Drive Change.</p>
        </div>
      </div>
    </footer>
  );
}
